import { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell } from "recharts";
import { Trophy, Medal } from "@phosphor-icons/react";
import api from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");
const rankColor = ["#FFAB00", "#9ca3af", "#b45309"];

function Table({ title, rows, columns }) {
  return (
    <div className="bg-white border border-[#111]">
      <div className="px-5 py-3 border-b border-[#111]">
        <h3 className="font-heading text-lg font-bold tracking-tight">{title}</h3>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-[#eee] text-left font-mono text-[10px] uppercase tracking-wider text-[#555]">
            <th className="px-4 py-2 w-10">#</th>
            {columns.map((c) => (
              <th key={c.key} className={`px-4 py-2 ${c.align === "right" ? "text-right" : ""}`}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f0f0f0]">
          {rows.length === 0 && (
            <tr><td colSpan={columns.length + 1} className="px-4 py-8 text-center text-[#999] font-mono text-sm">No data yet.</td></tr>
          )}
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-[#fafafa] transition-colors" data-testid={`leaderboard-row-${i}`}>
              <td className="px-4 py-3">
                {i < 3 ? <Medal size={18} weight="fill" color={rankColor[i]} /> : <span className="font-mono-data text-[#999]">{i + 1}</span>}
              </td>
              {columns.map((c) => (
                <td key={c.key} className={`px-4 py-3 ${c.align === "right" ? "text-right font-mono-data" : "font-medium"} ${c.accent || ""}`}>
                  {c.format ? c.format(r[c.key]) : r[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function Leaderboard() {
  const [data, setData] = useState({ agents: [], writers: [] });

  useEffect(() => {
    api.get("/leaderboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const agentChart = data.agents.slice(0, 8).map((a) => ({ name: a.name.split(" ")[0], value: a.collected }));
  const writerChart = data.writers.slice(0, 8).map((w) => ({ name: w.name.split(" ")[0], value: w.words }));

  return (
    <div className="p-6 md:p-8">
      <div className="mb-8 flex items-center gap-3">
        <Trophy size={28} weight="fill" color="#0047FF" />
        <div>
          <p className="font-mono text-xs tracking-widest text-[#0047FF]">PERFORMANCE</p>
          <h2 className="font-heading text-4xl font-bold tracking-tighter">Team Leaderboard</h2>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-[#111] p-6">
          <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Top Agents by Revenue Collected</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={agentChart}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#111" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#111" }} tickLine={false} />
              <Bar dataKey="value">{agentChart.map((_, i) => <Cell key={i} fill="#0047FF" />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="bg-white border border-[#111] p-6">
          <h3 className="font-heading text-lg font-bold tracking-tight mb-4">Top Writers by Words Delivered</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={writerChart}>
              <XAxis dataKey="name" tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#111" }} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fontFamily: "IBM Plex Mono" }} axisLine={{ stroke: "#111" }} tickLine={false} />
              <Bar dataKey="value">{writerChart.map((_, i) => <Cell key={i} fill="#00C853" />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Table
          title="Agents"
          rows={data.agents}
          columns={[
            { key: "name", label: "Agent" },
            { key: "leads", label: "Leads", align: "right" },
            { key: "booking", label: "Booked", align: "right", format: inr },
            { key: "collected", label: "Collected", align: "right", format: inr, accent: "text-[#00C853]" },
          ]}
        />
        <Table
          title="Writers"
          rows={data.writers}
          columns={[
            { key: "name", label: "Writer" },
            { key: "assignments", label: "Jobs", align: "right" },
            { key: "words", label: "Words", align: "right", format: (n) => Number(n).toLocaleString() },
            { key: "paid", label: "Paid", align: "right", format: inr, accent: "text-[#0047FF]" },
          ]}
        />
      </div>
    </div>
  );
}
